// src/components/RadarChart.tsx
import React from 'react';
import { Dimension, DIMENSION_LABELS, DIMENSION_ORDER } from '../data/quizQuestions';
import { DimensionScores } from '../lib/quizState';

interface RadarChartProps {
  scores: DimensionScores;
  size?: number;
}

// All dimensions have 2 probes = max 6 each.
// We normalize to a percentage of max so the radar is comparable across dimensions.
const DIMENSION_MAX: Record<Dimension, number> = {
  systems: 6, regulatory: 6, sponsor: 6, technical: 6, scope: 6,
};

const RadarChart: React.FC<RadarChartProps> = ({ scores, size = 480 }) => {
  const cx = size / 2;
  const cy = size / 2;
  const radius = (size / 2) * 0.7;
  const numAxes = DIMENSION_ORDER.length;

  const axisPoint = (i: number, ratio: number) => {
    // Start at top (-90deg) and go clockwise
    const angle = (Math.PI * 2 * i) / numAxes - Math.PI / 2;
    return {
      x: cx + radius * ratio * Math.cos(angle),
      y: cy + radius * ratio * Math.sin(angle),
      angle,
    };
  };

  const dataPoints = DIMENSION_ORDER.map((dim, i) => {
    const pct = scores[dim] / DIMENSION_MAX[dim];
    return axisPoint(i, pct);
  });

  const polygonPath = dataPoints.map(p => `${p.x},${p.y}`).join(' ');

  const gridLevels = [0.25, 0.5, 0.75, 1.0];

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="w-full max-w-[480px] mx-auto"
      role="img"
      aria-labelledby="radar-chart-title"
    >
      <title id="radar-chart-title">AI Readiness Assessment, 5-axis radar</title>
      {/* Grid rings */}
      {gridLevels.map((level, i) => (
        <polygon
          key={i}
          points={DIMENSION_ORDER.map((_, j) => {
            const p = axisPoint(j, level);
            return `${p.x},${p.y}`;
          }).join(' ')}
          fill="none"
          stroke="#12151C"
          strokeWidth={1}
        />
      ))}

      {/* Axis lines */}
      {DIMENSION_ORDER.map((_, i) => {
        const p = axisPoint(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#12151C" strokeWidth={1} />;
      })}

      {/* Data polygon */}
      <polygon
        points={polygonPath}
        fill="#C9A227"
        fillOpacity={0.18}
        stroke="#C9A227"
        strokeWidth={2}
        style={{ transition: 'all 320ms cubic-bezier(0.22, 0.61, 0.36, 1)' }}
      />

      {/* Data points */}
      {dataPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={4} fill="#C9A227" />
      ))}

      {/* Axis labels */}
      {DIMENSION_ORDER.map((dim, i) => {
        const p = axisPoint(i, 1.18);
        const anchor =
          Math.abs(p.x - cx) < 4 ? 'middle' :
          p.x > cx ? 'start' : 'end';
        return (
          <text
            key={dim}
            x={p.x}
            y={p.y}
            textAnchor={anchor}
            dominantBaseline="middle"
            fill="#7A8494"
            fontFamily="JetBrains Mono, monospace"
            fontSize={10}
            letterSpacing="0.1em"
            style={{ textTransform: 'uppercase' }}
          >
            {DIMENSION_LABELS[dim]}
          </text>
        );
      })}
    </svg>
  );
};

export default RadarChart;
